/* eslint-disable jsx-a11y/anchor-is-valid */
/* global cookieStore */
import React, { useEffect, useState } from 'react';
import { Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

import {
  getLegacyAssessments, getLegacyParticipants, getLegacyReports, getReviews, getUserSurveys,
} from '../../../bridge';
import { redirectToRespondent } from '../../../helpers/itpmetricsUtils';
import { logError } from '../../../helpers/logger';
import { __ } from '../../../helpers/translations';
import { createCookie, getCookieValue } from '../../../helpers/utils';
import { useAuthContext } from '../../../hooks/useAuthContext';
import ContentSection from '../common/ContentSection';
import Loading from '../common/Loading';
import Page from '../common/Page';
import SignedOut from '../common/SignedOutDialog';
import SurveyNotFound from '../common/SurveyNotFound';
import LockScreen from '../verification/LockScreen';

import AssessmentCard from './AssessmentCard';
import DashboardCard from './DashboardCard';
import LegacyReportCard from './LegacyReportCard';
import ParticipantCard from './ParticipantCard';

function Dashboard() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [surveys, setSurveys] = useState([]);
  const [respondents, setRespondents] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [legacyReports, setLegacyReports] = useState([]);
  const [legacyAssessments, setLegacyAssessments] = useState([]);
  const [legacyParticipants, setLegacyParticipants] = useState([]);
  const [isUnavailable, setIsUnavailable] = useState(false);
  const [error, setError] = useState(null);
  const { user } = useAuthContext();
  const row = getCookieValue('display-rows');

  useEffect(() => {
    const fetchSurveys = async () => {
      const { error: err, response } = await getUserSurveys();
      if (err) {
        logError('dashboard_fetch_surveys', err);
        setError(err);
        return;
      }
      const result = await redirectToRespondent();
      if (result === false) {
        setIsUnavailable(true);
        return;
      }

      setSurveys(response.filter((r) => r.type === 'survey' && !r.self));
      setRespondents(response.filter((r) => r.type === 'respondent'));

      setIsLoading(false);
    };

    const fetchReviews = async () => {
      const { error: err, response } = await getReviews();
      if (err) {
        logError('dashboard_fetch_reviews', err);
        return;
      }
      setReviews(response);
    };

    const fetchLegacyParticipants = async () => {
      const { error: err, response } = await getLegacyParticipants();
      if (err) {
        logError('dashboard_fetch_legacy_participants', err);
        return;
      }
      setLegacyParticipants(response.filter((item) => item.completed !== 2 || item.team_designer_assessment));
    };

    const fetchLegacyReports = async () => {
      const { error: err, response } = await getLegacyReports();
      if (err) {
        logError('dashboard_fetch_legacy_reports', err);
        return;
      }
      setLegacyReports(response);
    };

    const fetchLegacyAssessments = async () => {
      const { error: err, response } = await getLegacyAssessments();
      if (err) {
        logError('dashboard_fetch_legacy_assessments', err);
        return;
      }
      setLegacyAssessments(response);
    };

    if (!user) {
      return;
    }
    if (!user.policyId) {
      navigate('/profile');
    } else {
      fetchSurveys();
      fetchReviews();
      fetchLegacyReports();
      fetchLegacyAssessments();
      fetchLegacyParticipants();
    }
  }, [user]);

  if (error) {
    return <LockScreen show />;
  }

  if (isUnavailable) {
    return <SurveyNotFound />;
  }
  if (!user) {
    return <SignedOut />;
  }

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Page>
      {!respondents?.length && !legacyParticipants?.length && (
        <ContentSection>
          <Alert variant="warning" className="text-center">
            {__('G301', null, [user.email])}
            <br />
            <br />
            {__('G292')}
          </Alert>
        </ContentSection>
      )}
      <br />
      {respondents && respondents.length > 0 && (
        <ContentSection tightSpacing>
          <h4 style={{ textAlign: 'center' }}>{__('G13')}</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {respondents.map((item) => (
              <DashboardCard
                key={`dashboard-respondent-${item.type}-${item.respondentId}`}
                item={item}
              />
            ))}
          </div>
        </ContentSection>
      )}
      {surveys && surveys.length > 0 && (
        <ContentSection tightSpacing>
          <hr />
          <h4 style={{ textAlign: 'center' }}>{__('G291')}</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {surveys.map((item) => (
              <DashboardCard
                key={`dashboard-survey-${item.type}-${item.surveyId}`}
                item={item}
              />
            ))}
          </div>
        </ContentSection>
      )}
      {reviews && reviews.length > 0 && (
        <ContentSection tightSpacing>
          <hr />
          <h4 style={{ textAlign: 'center' }}>{__('G290')}</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {reviews.map((item) => (
              <DashboardCard
                key={`dashboard-review-${item.type}-${item.reviewId}`}
                item={item}
              />
            ))}
          </div>
        </ContentSection>
      )}
      {legacyParticipants && legacyParticipants.length > 0 && (
        <ContentSection tightSpacing>
          <hr />
          <h4 style={{ textAlign: 'center' }}>{__('G289')}</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {legacyParticipants.map((item) => (
              <ParticipantCard
                key={`dashboard-legacy-participant-${item.participant_guid}`}
                item={item}
              />
            ))}
          </div>
        </ContentSection>
      )}
      {legacyReports && legacyReports.length > 0 && (
        <ContentSection tightSpacing>
          <hr />
          <h4 style={{ textAlign: 'center' }}>{__('G288')}</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {legacyReports.map((item) => (
              <LegacyReportCard
                key={`dashboard-legacy-report-${item.respondentId}`}
                item={item}
              />
            ))}
          </div>
        </ContentSection>
      )}
      {legacyAssessments && legacyAssessments.length > 0 && (
        <ContentSection tightSpacing>
          <hr />
          <h4 style={{ textAlign: 'center' }}>{__('G293')}</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {legacyAssessments.map((item) => (
              <AssessmentCard
                key={`dashboard-legacy-assessment-${item.guid}`}
                item={item}
              />
            ))}
          </div>
        </ContentSection>
      )}
      <ContentSection>
        <div className="text-center">
          {row ? (
            <a
              className="opacity-50 px-1"
              href={null}
              onClick={async () => {
                await cookieStore.delete('display-rows');
                window.location.reload();
              }}
              style={{ cursor: 'pointer' }}
            >
              {__('G313')}
            </a>
          ) : (
            <a
              className="opacity-50 px-1"
              href={null}
              onClick={() => {
                createCookie('display-rows', true);
                window.location.reload();
              }}
              style={{ cursor: 'pointer' }}
            >
              {__('G312')}
            </a>
          )}
        </div>
      </ContentSection>
    </Page>
  );
}

export default Dashboard;
